import React from 'react';
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import ServiceSchema from "@/components/ServiceSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    Zap,
    FileText,
    Settings,
    Heart,
    Shield,
    Phone,
    CheckCircle,
    ArrowRight,
    ArrowLeft,
    Users,
    Award,
    TriangleAlert
} from "lucide-react"; 
const DentalImplants = () => {
    useScrollAnimation();

    const benefits = [
        "Permanent solution",
        "Natural feel",
        "Bone preservation",
        "High success rate"
    ];

    const implantProcess = [
        {
            title: "Consultation, Medical History & X-Rays",
            description: "At your consultation, we review your medical history, take X-rays, and assess suitability for treatment.",
            imageSrc: "/dental-consultation.png",
        },
        {
            title: "Placement",
            description: "The implant is gently positioned into the jawbone under local anaesthetic.",
            imageSrc: "/dental-checkup.png"
        },
        {
            title: "Healing",
            description: "Over several weeks or months, the bone bonds with the implant through a process called osseointegration, creating a strong foundation.",
            imageSrc: "/dental-care.png"
        },
        {
            title: "Restoration",
            description: "Once healing is complete, a custom crown, bridge, or denture is attached, designed to match your natural teeth.",
            imageSrc: "/dental-fillings.png"
        },
        {
            title: "Care",
            description: "Daily brushing, flossing, and regular dental visits help maintain the implant long term.",
            imageSrc: "/dental-care.png"
        }
    ];

    return (
        <div className="min-h-screen flex flex-col">
      <SEO title="Dental Implants Broadmeadows | Permanent Tooth Replacement" description="Dental implants at Y3 Smiles Dental in Broadmeadows. Replace missing teeth permanently — natural feel, bone-preserving & long-lasting. Book today." image="/og-reception.jpg" />

      <ServiceSchema
        serviceName="Dental Implants"
        description={"Dental implants at Y3 Smiles Dental in Broadmeadows. Replace missing teeth permanently — natural feel, bone-preserving & long-lasting. Book today."}
        url="https://y3smilesdental.com.au/treatments/dental-implants/"
        priceFromAUD={4000}
      />
            <Header />
            <main className="flex-grow">
                {/* Hero Section */}
                <section className="bg-gradient-hero py-24">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto text-center">
                            <h1 className="text-5xl md:text-6xl font-bold text-neutral-800 mb-6">
                                Dental Implants
                            </h1>
                            <p className="text-xl text-neutral-800 mb-8 leading-relaxed">
                                Dental implants are a modern option for replacing missing teeth. They are small titanium posts placed into the jawbone, acting like the root of a natural tooth. Once healed, a custom crown, bridge, or denture is attached, creating a secure, natural-looking replacement that blends with your smile.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4 mb-8">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2">
                                        <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                        <span className="text-sm font-medium text-neutral-700">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                            <Button variant="booking" size="xl" asChild>
                                <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>BOOK CONSULTATION</a>
                            </Button>
                        </div>
                    </div>
                </section>                {/* AHPRA Educational Introduction */}
                <section className="py-8 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <p className="text-blue-800 text-sm leading-relaxed">
                                    The information on this page is intended as general education about dental implants. Every patient&apos;s dental health is different, and your dentist will assess your individual needs before recommending any treatment. We encourage you to ask questions and, if you have any concerns, to seek a second opinion.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* What are Dental Implants */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                <div>
                                    <h2 className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">
                                        Modern Tooth Replacement
                                    </h2>
                                    <h3 className="text-4xl font-bold text-neutral-800 mb-6">
                                        Restoring Smiles with Confidence
                                    </h3>
                                    <p className="text-lg text-neutral-800 mb-8 leading-relaxed">
                                        Dental implants may help restore chewing, speech, and confidence. However, suitability depends on medical history, bone density, gum health, and overall wellbeing.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <h4 className="font-semibold text-neutral-800 mb-1">Permanent Solution</h4>
                                                <p className="text-neutral-800 text-sm">Long-lasting tooth replacement that can last a lifetime</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <h4 className="font-semibold text-neutral-800 mb-1">Natural Feel</h4>
                                                <p className="text-neutral-800 text-sm">Functions and feels like your natural teeth</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <h4 className="font-semibold text-neutral-800 mb-1">Bone Preservation</h4>
                                                <p className="text-neutral-800 text-sm">Helps maintain jawbone structure and facial support</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="aspect-video bg-neutral-100 rounded-2xl overflow-hidden">
                                    <picture>
                                        <source type="image/webp" srcSet="/optimized/dental-implants-800w.webp 800w, /optimized/dental-implants-1600w.webp 1600w" sizes="(max-width: 1024px) 100vw, 640px" />
                                        <img
                                            src="/optimized/dental-implants.webp"
                                            alt="Dental implant procedure and restoration"
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </picture>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Treatment Process */}
                <section className="py-24 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                The Implant Process (Simplified)
                            </h2>
                            <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                                A comprehensive approach to ensure the best possible outcome for your dental implant treatment.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {implantProcess.map((step, index) => {
                                return (
                                    <Card key={index} className="border-0 shadow-soft hover:shadow-medium hover:scale-[1.02] hover:-translate-y-1 transition-smooth fade-in-section" style={{ transitionDelay: `${index * 150}ms` }}>
                                        <CardHeader>
                                            <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                                                <img src={step.imageSrc} alt={step.title} className="w-8 h-8" />
                                            </div>
                                            <CardTitle className="text-xl font-bold text-neutral-800 mb-2">
                                                {step.title}
                                            </CardTitle>
                                            <p className="text-neutral-800 leading-relaxed text-sm">
                                                {step.description}
                                            </p>
                                        </CardHeader>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Am I a Candidate? */}
                <section className="py-16 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                                Am I a Candidate for Dental Implants?
                            </h2>
                            <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                                Patients who are generally good candidates for dental implants include those in good overall and oral health, with adequate bone density in the jaw, healthy gum tissue, and a commitment to ongoing aftercare and regular checkups. Non-smokers tend to have better outcomes. A thorough assessment including X-rays is needed to determine whether implants are suitable for you.
                            </p>
                            <div className="bg-neutral-50 rounded-lg p-6 border border-neutral-200">
                                <p className="text-neutral-700 text-sm leading-relaxed italic">
                                    Your dentist can assess whether implants are appropriate for your individual situation. A proper assessment including X-rays, medical history review, and examination of bone and gum health is always carried out before any implant treatment is recommended.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Disclaimer */}
                <section className="py-16 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-yellow-800 mb-2 flex items-center">
                                    <TriangleAlert className="w-5 h-5 mr-2" />
                                    Disclaimer
                                </h3>
                                <p className="text-yellow-700 text-sm leading-relaxed">
                                    All dental treatments carry risks. Please consult with your treating dentist to understand whether dental implants are appropriate for your individual situation.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-24 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                    Frequently Asked Questions
                                </h2>
                            </div>
                            <Accordion type="single" className="w-full space-y-4">
                                <AccordionItem value="faq-0" className="bg-white rounded-lg shadow-soft border-0">
                                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                                        <span className="text-lg font-semibold text-neutral-800">Are dental implants painful?</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-4">
                                        <p className="text-neutral-800 leading-relaxed">The implant procedure is performed under local anaesthetic, so most patients report minimal discomfort during the process. Some soreness and swelling in the days following surgery is normal and manageable with over-the-counter pain relief. Your dentist will give you detailed aftercare instructions.</p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value="faq-1" className="bg-white rounded-lg shadow-soft border-0">
                                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                                        <span className="text-lg font-semibold text-neutral-800">How long do dental implants last?</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-4">
                                        <p className="text-neutral-800 leading-relaxed">With good oral hygiene and regular dental checkups, dental implants can last many years. Longevity depends on individual factors including bone health, oral hygiene habits, and lifestyle. Your dentist can discuss what to expect based on your specific situation.</p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value="faq-2" className="bg-white rounded-lg shadow-soft border-0">
                                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                                        <span className="text-lg font-semibold text-neutral-800">Does Medicare or private health cover dental implants?</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-4">
                                        <p className="text-neutral-800 leading-relaxed">Dental implants are generally not covered by Medicare. Some private health funds offer partial cover under major dental. We recommend checking with your health fund before your consultation. Our team can provide a detailed treatment plan with costs so you can make an informed decision.</p>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </div>
                </section>

                {/* Internal Linking Block */}
                <section className="py-12 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-neutral-800 mb-4">Explore our services:</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: "Children's Dentistry", to: "/treatments/childrens-dentistry" },
                                    { label: "Emergency Dentist", to: "/treatments/emergency" },
                                    { label: "Dental Fillings", to: "/treatments/dental-fillings" },
                                    { label: "Root Canal Treatment", to: "/treatments/root-canal" },
                                    { label: "Crowns and Bridges", to: "/treatments/crown-bridge" },
                                    { label: "Wisdom Teeth Removal", to: "/treatments/wisdom-teeth" },
                                    { label: "Teeth Whitening", to: "/treatments/teeth-whitening" },
                                    { label: "All Treatments", to: "/treatments" },
                                ].map((link) => (
                                    <Link key={link.to} to={link.to} className="text-sm text-primary hover:underline font-medium py-1">
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-24 bg-neutral-50">
                    <div className="container mx-auto px-6 text-center">
                        <div className="max-w-3xl mx-auto">
                            <h2 className="text-4xl font-bold text-neutral-800 mb-6">
                                Interested in Dental Implants?
                            </h2>
                            <p className="text-lg text-neutral-800 mb-8">
                                Schedule a consultation to discuss whether dental implants are the right solution for your individual needs.
                            </p>
                            <Button variant="booking" size="xl" asChild>
                                <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                    SCHEDULE CONSULTATION
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </a>
                            </Button>
                        </div>
                    </div>
                </section>

                {/* <Contact /> */}
            </main>
            <Footer />
        </div>
    );
};

export default DentalImplants;
